import React, { useMemo, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import ReusableTabs from '../common/ReusableTabs';
import investorRelationHeaderImage from '../../static/investor_image/investorHeader.png';
import * as styles from '../productPage/tableComponents/ProductPage_style';
import {
    GET_RESOURCE_LIBRARY_ARTICLE_ITEM_QUERY,
    getResourceLibraryArticleItemQueryVariables,
} from '../../graphql/pimcore/modules/resourceLibrary/queries/getResourceLibraryArticleItemQuery';
import pimcoreClient from '../../graphql/pimcore/apollo/pimcoreClient';
import { transformResourceLibraryArticleItemResponse } from '../../graphql/pimcore/modules/resourceLibrary/tools/transformResourceLibraryArticleItemResponse';
import { ResourceLibraryArticleItem } from '../../graphql/pimcore/modules/resourceLibrary/types/resourceLibraryItems';
import { findLocale, LocaleCode } from '../../utils/Locale';
import DebouncedLoader from '../common/DebouncedLoader';
import { ResourceLibraryItemBreadCrumbs } from '../common/BreadCrumbs';
import pageStyles from './ArticleResourcePage.module.css';
import { fixPimcoreRootRelativeSrcs, sanitizeHtml } from '../../utils/htmlUtils';
import {
    collectPimcoreAssetIdsFromHtmlBlocks,
    replacePimcoreAssetPlaceholders,
    usePimcoreAssets,
} from '../../utils/pimcoreAssetPlaceholders';
import { toUrl } from '@/utils/convertImagePath';

interface ArticleResourcePageProps {
    id: string;
    locale: string;
}

interface ProcessedSection {
    tocTitle: string; // title for TOC (if present)
    headerText: string; // text to render in header (empty if content is missing)
    level: number; // heading level (1-6)
    content: string; // combined HTML content
}

const ArticleResourcePage: React.FC<ArticleResourcePageProps> = ({ id, locale }) => {
    const localeCode: LocaleCode = findLocale(locale).locale;

    // Query for the article item
    const {
        loading: isItemLoading,
        error: itemError,
        data: itemData,
    } = useQuery(GET_RESOURCE_LIBRARY_ARTICLE_ITEM_QUERY, {
        variables: getResourceLibraryArticleItemQueryVariables(localeCode, Number(id)),
        client: pimcoreClient,
    });

    useEffect(() => {
        if (itemError) {
            console.error('GraphQL Article Query Error:', itemError);
        }
    }, [itemError]);

    // Transform the raw article response
    const articleItem = useMemo<ResourceLibraryArticleItem | null>(() => {
        if (!itemData) return null;
        const item = transformResourceLibraryArticleItemResponse(itemData);
        if (!item || !item.contentSections) {
            return item;
        }
        return {
            ...item,
            contentSections: item.contentSections.map((section) => ({
                ...section,
                content: section.content
                    ? fixPimcoreRootRelativeSrcs(section.content, true)
                    : section.content,
            })),
        };
    }, [itemData]);

    const assetIdsFromSections = useMemo<string[]>(() => {
        if (!articleItem || !articleItem.contentSections) {
            return [];
        }

        const htmlBlocks = articleItem.contentSections.map((section) => section.content ?? '');
        return collectPimcoreAssetIdsFromHtmlBlocks(htmlBlocks);
    }, [articleItem]);

    const {
        assetsById,
        loading: isAssetsLoading,
        error: assetsError,
    } = usePimcoreAssets(assetIdsFromSections, { client: pimcoreClient });

    const articleItemWithAssets = useMemo<ResourceLibraryArticleItem | null>(() => {
        if (!articleItem) {
            return null;
        }

        if (assetsById.size === 0) {
            return articleItem;
        }

        const updatedSections = articleItem.contentSections.map((section) => {
            if (!section.content) {
                return section;
            }

            const updatedContent = replacePimcoreAssetPlaceholders(section.content, assetsById);
            if (updatedContent === section.content) {
                return section;
            }

            return {
                ...section,
                content: updatedContent,
            };
        });

        return {
            ...articleItem,
            contentSections: updatedSections,
        };
    }, [articleItem, assetsById]);

    // Process sections to handle empty titles and contents:
    // - If both title and content are missing, omit the section.
    // - If title is missing, merge its content with the previous section.
    // - If content is missing but title exists, render an empty header.
    const processedSections = useMemo<ProcessedSection[]>(() => {
        if (!articleItemWithAssets || !articleItemWithAssets.contentSections) return [];
        const groups: ProcessedSection[] = [];

        articleItemWithAssets.contentSections.forEach((section) => {
            const titleExists = section.title && section.title.trim() !== '';
            const contentExists = section.content && section.content.trim() !== '';

            if (!titleExists && !contentExists) {
                return;
            }

            if (titleExists) {
                // Start a new group
                groups.push({
                    tocTitle: section.title!,
                    headerText: section.title!, // will adjust below if content is empty
                    level: Math.min(Math.max(section.level, 1), 6),
                    content: section.content ? section.content : '',
                });
            } else {
                // Title is missing but content exists: merge with previous group if available
                if (groups.length > 0) {
                    groups[groups.length - 1]!.content += section.content ? section.content : '';
                } else {
                    // No previous group: create a group with no title
                    groups.push({
                        tocTitle: '',
                        headerText: '',
                        level: Math.min(Math.max(section.level, 1), 6),
                        content: section.content ? section.content : '',
                    });
                }
            }
        });

        return groups;
    }, [articleItemWithAssets]);

    // Determine if multiple section levels exist (i.e., not all sections are H1)
    const multiLevelTOC = useMemo<boolean>(() => {
        if (!processedSections || processedSections.length === 0) return false;
        return processedSections.some((section) => section.level !== 1);
    }, [processedSections]);

    const toc = processedSections.length > 1 && (
        <nav className={pageStyles.toc}>
            <h3>
                <a href="#article-beginning">{articleItemWithAssets?.name}</a>
            </h3>
            <ul>
                {processedSections.map((group, index) => {
                    if (!group.tocTitle.trim()) return null;
                    const sectionId = `section-${index}-${group.tocTitle.replace(/\s+/g, '-').toLowerCase()}`;
                    return (
                        <li key={sectionId}>
                            <a href={`#${sectionId}`}>
                                {multiLevelTOC && group.level === 1 ? (
                                    <strong>{group.tocTitle}</strong>
                                ) : (
                                    group.tocTitle
                                )}
                            </a>
                        </li>
                    );
                })}
            </ul>
        </nav>
    );

    // Render the processed sections. We add an anchor with id "article-beginning" at the top.
    const renderSections = () => {
        if (!processedSections.length) return null;
        return (
            <>
                <div id="article-beginning" className={pageStyles.articleBeginning} />
                {processedSections.map((group, index) => {
                    const sectionId = group.tocTitle
                        ? `section-${index}-${group.tocTitle.replace(/\s+/g, '-').toLowerCase()}`
                        : `section-${index}`;
                    const HeadingTag = `h${group.level}` as keyof JSX.IntrinsicElements;
                    const sanitizedContent = sanitizeHtml(group.content || '');

                    return (
                        <div key={sectionId} id={sectionId} className={pageStyles.articleSection}>
                            {group.tocTitle ? <HeadingTag>{group.headerText}</HeadingTag> : null}
                            {sanitizedContent && (
                                <div dangerouslySetInnerHTML={{ __html: sanitizedContent }} />
                            )}
                        </div>
                    );
                })}
            </>
        );
    };

    const isLoading = isItemLoading || isAssetsLoading;

    if (!isLoading) {
        if (itemError) return <div className="mid-width">Error: {itemError.message}</div>;
        if (assetsError) {
            const message =
                assetsError instanceof Error ? assetsError.message : 'Failed to load assets.';
            return <div className="mid-width">Error: {message}</div>;
        }
        if (!articleItem) return <div className="mid-width">Article not found.</div>;
    }

    return (
        <DebouncedLoader loading={isLoading} delay={1000} size={40} centered={true}>
            <ReusableTabs
                backgroundColor="#ffffff"
                bannerImage={toUrl(investorRelationHeaderImage)}
                bannerTitle={articleItem?.name || ''}
                bannerDescription={articleItem?.description || ''}
                tabLabels={[]}
                tabKeys={['']}
                tabContents={[
                    <div className="mid-width article-content-wrapper" key="article">
                        <div className={pageStyles.articleLayout}>
                            {toc && <div className={pageStyles.tocContainer}>{toc}</div>}
                            <div className={pageStyles.mainContent}>{renderSections()}</div>
                        </div>
                    </div>,
                ]}
                bannerFontStyle={styles.bannerFont}
                subBannerFontStyle={styles.bannerFont2}
                pageName="article"
                showTabs={false}
                breadcrumbs={
                    articleItem?.navigationNodeId ? (
                        <ResourceLibraryItemBreadCrumbs item={articleItem} />
                    ) : null
                }
            />
        </DebouncedLoader>
    );
};

export default ArticleResourcePage;
